/**
 * Company-declared facts: supplied by the owner (Büşra Aktaş) on 2026-11-03, not computed from repo data.
 * Shared by the trust block, About, Organization JSON-LD and scripts/generate-entity-jsonld.mjs.
 * Only the owner's statements belong here; no certificate names/IDs, partnerships, ratings or superlatives.
 */
export const COMPANY_DECLARED_SOURCE = {
  source: 'company-declared',
  declaredBy: 'Büşra Aktaş',
  declaredOn: '2026-11-03',
  caption: { tr: 'Kurum beyanı · Kasım 2026', en: 'Company-declared · November 2026' },
};

/** Lower bounds as declared ("86+", "60+", "32+"); fullTimeEmployees is exact. */
export const COMPANY_DECLARED_COUNTS = {
  completedProjects: 86,
  clients: 60,
  outsourcedSpecialists: 32,
  fullTimeEmployees: 6,
};

/** Anonymous roles only: team members asked not to be named or pictured. */
export const COMPANY_TEAM_ROLES = [
  { count: 2, title: { tr: 'Full Stack Engineer', en: 'Full Stack Engineer' } },
  { count: 1, title: { tr: 'Staff Frontend Engineer', en: 'Staff Frontend Engineer' } },
  { count: 1, title: { tr: 'Staff System & DevOps Engineer', en: 'Staff System & DevOps Engineer' } },
  { count: 1, title: { tr: 'Senior Backend Engineer', en: 'Senior Backend Engineer' } },
  { count: 1, title: { tr: 'Senior Mobile Development Engineer', en: 'Senior Mobile Development Engineer' } },
];

export const COMPANY_CLIENT_COUNTRIES = [
  { tr: 'Almanya', en: 'Germany' },
  { tr: 'Türkiye', en: 'Turkey' },
  { tr: 'Hollanda', en: 'Netherlands' },
  { tr: 'BAE (Dubai)', en: 'UAE (Dubai)' },
  { tr: 'İspanya', en: 'Spain' },
  { tr: 'Polonya', en: 'Poland' },
];

/** Issuers of individual certifications held by team members, not company-level certification. */
export const TEAM_CERTIFICATION_ISSUERS = ['AWS', 'Google', 'IBM', 'Cloudflare', 'Meta'];

/**
 * Technologies we work with, grouped for display: no partnership program is implied.
 * Owner-declared on 2026-11-03, except TECHNOLOGIES_FROM_PUBLISHED_WORK, which are named in the
 * published service scopes (serviceCatalog.ts). Items are plain names or { tr, en } for expertise terms.
 */
export const TECHNOLOGY_GROUPS = [
  {
    id: 'ai',
    label: { tr: 'Yapay zeka', en: 'AI' },
    items: [
      { tr: 'Yapay zeka implementasyonu', en: 'AI implementation' },
      { tr: 'Yapay zeka model eğitimi ve fine-tuning', en: 'AI model training and fine-tuning' },
      'LangChain',
      'LM Studio',
      'OpenAI',
      'Anthropic Claude',
    ],
  },
  {
    id: 'frontend',
    label: { tr: 'Arayüz, mobil ve tasarım', en: 'Frontend, mobile and design' },
    items: ['React', 'Next.js', 'React Native', 'Kotlin', 'Figma'],
  },
  { id: 'backend', label: { tr: 'Backend', en: 'Backend' }, items: ['Node.js', 'Go', 'Java', 'Kafka'] },
  { id: 'data', label: { tr: 'Veri', en: 'Data' }, items: ['PostgreSQL', 'MySQL', 'Microsoft SQL Server', 'Supabase'] },
  {
    id: 'cloud',
    label: { tr: 'Bulut ve platformlar', en: 'Cloud and platforms' },
    items: ['AWS', 'Vercel', 'Cloudflare', 'Google Cloud'],
  },
  {
    id: 'payments',
    label: { tr: 'Ödeme altyapıları', en: 'Payments' },
    items: ['Stripe', 'iyzico', 'PayTR', 'Paddle'],
  },
];

/** Not in the owner's 2026-11-03 list; each name appears in a published service scope in serviceCatalog.ts. */
export const TECHNOLOGIES_FROM_PUBLISHED_WORK = ['OpenAI', 'Anthropic Claude', 'Figma'];

function localizeTechnology(item, lang) {
  return typeof item === 'string' ? item : item[lang];
}

export function getTechnologyGroups(lang) {
  return TECHNOLOGY_GROUPS.map((group) => ({
    id: group.id,
    label: group.label[lang],
    items: group.items.map((item) => localizeTechnology(item, lang)),
  }));
}

/** Flat, de-duplicated list for Organization knowsAbout and llms.txt. */
export function getTechnologiesWeWorkWith(lang) {
  return [...new Set(getTechnologyGroups(lang).flatMap((group) => group.items))];
}

/** Process statement; comparative or superlative outcome claims are not provable, so none are made. */
export const DELIVERY_OUTCOME_APPROACH = {
  tr: 'Yeni projelerde yapay zeka görünürlüğü (GEO) ve SEO hedefleri teslim kapsamına dahildir; her teslim e2e testler ve QA ekibimizin raporlarıyla görsel olarak belgelenir.',
  en: 'In new projects, AI visibility (GEO) and SEO goals are part of the delivery scope; every delivery is documented visually with end-to-end tests and reports from our QA team.',
};

function joinList(items, lang) {
  if (items.length < 2) return items.join('');
  return `${items.slice(0, -1).join(', ')} ${lang === 'tr' ? 've' : 'and'} ${items[items.length - 1]}`;
}

export function formatTeamCertifications(lang) {
  const issuers = joinList(TEAM_CERTIFICATION_ISSUERS, lang);
  return lang === 'tr'
    ? `Ekipte ${issuers} sertifikalı mühendisler`
    : `Engineers on the team hold ${issuers} certifications`;
}

export function formatClientCountries(lang) {
  return joinList(
    COMPANY_CLIENT_COUNTRIES.map((country) => country[lang]),
    lang,
  );
}

/** One-line summary for AI answers and llms.txt. */
export function getCompanyDeclaredSummary(lang) {
  const c = COMPANY_DECLARED_COUNTS;
  if (lang === 'tr') {
    return `Kurum beyanına göre (Kasım 2026): ${c.completedProjects}+ tamamlanan proje, ${c.clients}+ müşteri (${formatClientCountries('tr')}), ${c.fullTimeEmployees} kişilik tam zamanlı ekip ve ${c.outsourcedSpecialists}+ uzmandan oluşan dış kaynak ağı.`;
  }
  return `Company-declared (November 2026): ${c.completedProjects}+ completed projects, ${c.clients}+ clients (${formatClientCountries('en')}), a full-time team of ${c.fullTimeEmployees} and an outsourced network of ${c.outsourcedSpecialists}+ specialists.`;
}
